import { useState } from 'react'
import type { Shot, JobStatus, SequenceJobStatus, MergeJobStatus, TransitionConfig } from '../types'
import { ShotCard } from './ShotCard'
import { MergePanel } from './MergePanel'

interface Props {
  grouped: Record<string, Shot[]>
  jobStatus: Record<string, JobStatus>
  sequenceJobs: Record<string, SequenceJobStatus>
  mergeJobs: Record<string, MergeJobStatus>
  projectId: string
  onGenerate: (shot: Shot, promptOverride: string, referenceAssetIds?: string[]) => void
  onGenerateVideo: (videoId: string) => void
  onGenerateSequence: (group: string, shots: Shot[], opts?: { characterAssetIds?: string[]; sceneAssetIds?: string[] }) => void
  onDismissSequenceError?: (seqKey: string) => void
  onClearSequence?: (seqKey: string) => void
  onMerge: (group: string, shots: Shot[], transitions: TransitionConfig[]) => void
  onClearMerge: (key: string) => void
}

const DEFAULT_JOB: JobStatus = { state: 'idle', elapsed: 0, error: null, file: null, logs: [] }

export function ShotGrid({
  grouped, jobStatus, sequenceJobs, mergeJobs, projectId,
  onGenerate, onGenerateVideo, onGenerateSequence, onDismissSequenceError, onClearSequence,
  onMerge, onClearMerge,
}: Props) {
  const videoIds = Object.keys(grouped).sort()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggle = (videoId: string) => {
    setExpanded((prev) => ({ ...prev, [videoId]: !prev[videoId] }))
  }

  if (videoIds.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-forge-ash text-sm">
        No scripts found for this project. Check the scriptsDir in projects.json.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {videoIds.map((videoId) => {
        const shots = grouped[videoId]
        const isOpen = !!expanded[videoId]
        const generatedCount = shots.filter(
          (s) => jobStatus[`${projectId}/${s.videoId}/${s.shotId}`]?.state === 'done'
        ).length
        const sequenceGroups = buildSequenceGroups(shots)

        return (
          <section key={videoId} className="border border-forge-border/50 rounded-xl overflow-hidden bg-forge-obsidian/30">
            {/* Collapsible header */}
            <button
              onClick={() => toggle(videoId)}
              className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-forge-obsidian/50 transition-colors"
            >
              <svg
                width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                className={`text-forge-ember transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <h2 className="font-display font-bold text-base text-forge-primary">{videoId}</h2>
              <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-forge-ember/10 text-forge-ember">
                {shots.length} shots
              </span>
              <span className="text-[11px] text-forge-ash">{shots[0]?.aspectRatio}</span>
              <span className="text-[11px] text-forge-ash">
                {generatedCount}/{shots.length} generated
              </span>
              {sequenceGroups.length > 0 && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-forge-gold/10 text-forge-gold">
                  {sequenceGroups.length} sequence{sequenceGroups.length > 1 ? 's' : ''}
                </span>
              )}
              <div className="flex-1" />
              <span
                onClick={(e) => { e.stopPropagation(); onGenerateVideo(videoId) }}
                className="text-[11px] font-semibold text-forge-ember border border-forge-border rounded-md px-3 py-1 hover:bg-forge-basalt transition-colors"
              >
                Generate {videoId}
              </span>
            </button>

            {/* Collapsible content */}
            {isOpen && (
              <div className="px-5 pb-5 pt-1 border-t border-forge-border/30">
                {/* Merge group panels (replaces SequencePanel) */}
                {sequenceGroups.map((sg) => {
                  const mergeKey = `${projectId}/${videoId}/${sg.group}-merged`
                  return (
                    <MergePanel
                      key={sg.group}
                      group={sg.group}
                      shots={sg.shots}
                      jobStatus={jobStatus}
                      projectId={projectId}
                      mergeJob={mergeJobs[mergeKey] ?? null}
                      onMerge={(transitions) => onMerge(sg.group, sg.shots, transitions)}
                      onClear={() => onClearMerge(mergeKey)}
                    />
                  )
                })}

                {/* Shot cards — never locked; generate independently, merge after */}
                <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
                  {shots.map((shot) => {
                    const shotKey = `${projectId}/${shot.videoId}/${shot.shotId}`
                    const mergeKey = shot.group ? `${projectId}/${videoId}/${shot.group}-merged` : null
                    const mergeState = mergeKey ? mergeJobs[mergeKey]?.state : undefined
                    const locked = mergeState === 'merging' || mergeState === 'uploading'
                    return (
                      <ShotCard
                        key={shot.shotId}
                        shot={shot}
                        job={jobStatus[shotKey] || DEFAULT_JOB}
                        projectId={projectId}
                        onGenerate={(promptOverride, assetIds) => onGenerate(shot, promptOverride, assetIds)}
                        lockedBySequence={locked}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

interface SequenceGroup {
  group: string
  shots: Shot[]
}

function buildSequenceGroups(shots: Shot[]): SequenceGroup[] {
  const map = new Map<string, Shot[]>()
  for (const s of shots) {
    if (!s.group) continue
    const arr = map.get(s.group) || []
    arr.push(s)
    map.set(s.group, arr)
  }
  const groups: SequenceGroup[] = []
  for (const [group, groupShots] of map) {
    if (groupShots.length < 2) continue
    groupShots.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    groups.push({ group, shots: groupShots })
  }
  return groups
}
